import React, { useState, useEffect, useRef } from 'react';
import { shallowEqual } from 'react-redux';
import * as THREE from 'three';
import { CustomEventTypes, LayoutOrientations, SimulationStates, defaultPartsCatalogObject } from '../../globals';
import { StoreActions } from '../../state/store';
import Panel from '../layout/Panel';
import { useTypedDispatch, useTypedSelector } from '../../state/hooks';
import Engine from '../../classes/Engine';
import MediaPanel from '../../classes/MediaPanel';
import ARTracker from '../../classes/ARTracker';
import Text3D from '../../classes/Text3D';
import ScopeLight from '../../classes/Light';
import ScopeCamera from '../../classes/Camera';
import AudioSource from '../../classes/AudioSource';
import PropertyGroup from '../properties/PropertyGroup';
import { Catalog, IPartsCatalogSceneObject } from '../../interfaces/PartsCatalog';
import { EyeIcon, LightBulbIcon, SearchIcon, ChevronRight, IconFillStyles, EyeIconOff, ClearIcon } from '../Iconset';
import Spacer from '../layout/Spacer';
import Separator from '../layout/Separator';
import StringProperty from '../properties/StringProperty'
import TextVariables from '../../classes/TextVariables';
import PartsCatalogUtils from '../../classes/utilities/PartsCatalogUtils';
import Button from '../layout/Button';
import { FeatureFlagNames } from '../../interfaces/enums';
import useCurrentStep from '../reactUtils/useCurrentStep';
import useForceUpdate from '../reactUtils/useForceUpdate';
import useEventListener from '../reactUtils/useEventListener';
import SessionUtils from '../../classes/utilities/SessionUtils';

const idListEquals = (prevArray: string[], nextArray: string[]) : boolean => 
{
	if (prevArray === nextArray) 
	{
		  return true;
	}
	  
	if (prevArray.length !== nextArray.length) 
	{
		  return false;
	}
	  
	for (let i = 0; i < prevArray.length; i++) 
	{
		if (prevArray[i] !== nextArray[i]) 
		{
			return false;
		}
	}
	  
	return true;
}

export default function PartsCatalogPreviewContainer(props)
{
	const dispatch = useTypedDispatch();
	const currentStep = useCurrentStep();
	const forceUpdate = useForceUpdate();
	
	const simulationState = useTypedSelector(state => state.applicationModule.simulationState);
	const partsCatalogSettings : Catalog = useTypedSelector(state => state.partsCatalogModule.catalogs[currentStep?.id]);
	const simulationHiddenObjects : string[] = useTypedSelector(state => 
	{
		if(currentStep?.id)
		{
			return state.partsCatalogModule.simulationHiddenObjects[currentStep.id] ?? [];
		}
		return [];
	}, idListEquals);
	const simulationHighlightedObjects : string[] = useTypedSelector(state => 
	{
		if(currentStep?.id)
		{
			return state.partsCatalogModule.simulationHighlightedObjects[currentStep.id] ?? [];
		}
		return [];
	}, idListEquals);
	const simulationIsolatedObjects : string[] = useTypedSelector(state => 
	{
		if(currentStep?.id)
		{
			return state.partsCatalogModule.simulationIsolatedObjects[currentStep.id] ?? [];
		}
		return [];
	}, idListEquals);
	const simulationExpandedObjects : string[] = useTypedSelector(state => state.partsCatalogModule.simulationExpandedObjects, shallowEqual);
	const selectedPart : IPartsCatalogSceneObject | null = useTypedSelector(state => state.partsCatalogModule.selectedPart[currentStep?.id]);
	const highlightedPart : string = useTypedSelector(state => state.partsCatalogModule.highlightedPartId);

	const [searchTerm, setSearchTerm] = useState('');
	const [showSearch, setShowSearch] = useState(false);
	const [previousStep, setPreviousStep] = useState(currentStep);

	useEventListener(CustomEventTypes.simulationStopped, handleOnSimulationStopped, document);

	function handleOnSimulationStopped()
	{
		dispatch(StoreActions.partsCatalog.clearSimulationObjects());
		dispatch(StoreActions.partsCatalog.clearSimulationExpandedObjects());
		dispatch(StoreActions.partsCatalog.setShowCollapseAll(false));
		PartsCatalogUtils.OnSimulationStopped();
	}

	useEffect(() =>
	{
		if(simulationState !== SimulationStates.Playing) return;
		PartsCatalogUtils.CatalogOpened(currentStep.id);

		return () => 
		{
			PartsCatalogUtils.CatalogClosed(currentStep.id);
		};
	}, []);

	useEffect(() =>
	{
		if(simulationState !== SimulationStates.Playing) return;
		if(previousStep && previousStep.id !== currentStep.id)
		{
			PartsCatalogUtils.CatalogClosed(previousStep.id);
			PartsCatalogUtils.CatalogOpened(currentStep.id);
		}
		setPreviousStep(currentStep);
	}, [currentStep]);

	useEffect(() =>
	{
		if(simulationState !== SimulationStates.Playing) return;
		PartsCatalogUtils.buildCatalogObjectsVisibility();
	}, [simulationHiddenObjects, simulationIsolatedObjects, simulationHighlightedObjects]);


	useEffect(() =>
	{
		if(highlightedPart)
		{
			expandAndScrollToPart(highlightedPart);
		}
	}, [highlightedPart]);

	useEffect(() =>
	{
		if(simulationState !== SimulationStates.Playing) return;
		if(selectedPart)
		{
			PartsCatalogUtils.PartFocus(selectedPart.id);
		}
		else
		{
			PartsCatalogUtils.PartFocus(null);
		}
	}, [selectedPart]);

	function expandAndScrollToPart(partId: string)
	{
		function expandToPart(part: IPartsCatalogSceneObject)
		{
			const parent = PartsCatalogUtils.GetFlattenedPart(currentStep.id, part.parent);
			if(parent)
			{
				if(!simulationExpandedObjects.includes(parent.id))
				{

					dispatch(StoreActions.partsCatalog.addSimulationExpandedObject(parent.id));
				}
				expandToPart(parent);
			}
		}

		const flattenedPart = PartsCatalogUtils.GetFlattenedPart(currentStep.id, partId);
		
		if(flattenedPart && flattenedPart.parent)
		{
			expandToPart(flattenedPart);
		}

		forceUpdate();
	}

	function shouldRender (object) : boolean 
	{
		const invalidClasses = [MediaPanel, ARTracker, Text3D, ScopeLight, ScopeCamera, AudioSource];
		if (!invalidClasses.some((invalidClass) => object instanceof invalidClass))
		{
			return true;
		}

		return false;
	}

	function buildHierarchy(root : THREE.Object3D, depth = 0)
	{
		if (!root)
			return [];

		const sceneRoot = Engine.getInstance().getSelectionRoot(currentStep?.scene?.userData.guid);
		const renderObjects: IPartsCatalogSceneObject[] = [];
		for (let index = 0; index < root.children.length; index++)
		{
			const child = root.children[index];

			if (child.userData.hideInHierarchy)
			{
				continue;
			}

			const grandChildren = buildHierarchy(child, depth+1);
			if((!child.userData.partsCatalog || child.userData.partsCatalog.included === true) && shouldRender(child))
			{
				const catalogObject: IPartsCatalogSceneObject = {
					name: child.name,
					children: grandChildren,
					userData: {
						guid: child.userData.guid,
						metaData: {...child.userData.metaData},
						partsCatalog: child.userData.partsCatalog ? {...child.userData.partsCatalog} : defaultPartsCatalogObject
					},
					id: child.userData.guid,
					depth: depth,
					expanded: simulationExpandedObjects.includes(child.userData.guid),
					nextPart: null,
					previousPart: null,
					parent: child.parent?.userData?.guid !== sceneRoot.userData.guid ? child.parent?.userData?.guid : ''
				}
				
				renderObjects.push(catalogObject);
				continue;
			}

			if((child.userData.partsCatalog && child.userData.partsCatalog.included === false) || !shouldRender(child))
			{
				for(const grandChild of grandChildren)
				{
					grandChild.parent = root.userData?.guid !== sceneRoot.userData.guid ? root.userData?.guid : '';
					renderObjects.push(grandChild);
				}
			}

		}

		return renderObjects;
	}

	function flattenHierarchy(parts: IPartsCatalogSceneObject[]) : IPartsCatalogSceneObject[]
	{
		const flattenedParts : IPartsCatalogSceneObject[] = [];
		for(const part of parts)
		{
			flattenedParts.push(part);
			if(part.children.length > 0)
			{
				flattenedParts.push(...flattenHierarchy(part.children));
			}
		}

		for(let i = 0; i < flattenedParts.length; i++)
		{
			flattenedParts[i].nextPart = flattenedParts[i+1]?.id ?? null;
			flattenedParts[i].previousPart = flattenedParts[i-1]?.id ?? null;
		}

		return flattenedParts;
	}

	const renderHierarchy = (() =>
	{
		function createItemsForChildren(parent: IPartsCatalogSceneObject, filter: string = searchTerm) : JSX.Element | JSX.Element[]
		{
			const items : JSX.Element[] = [];
			for (const child of parent.children)
			{
				const childItems = createItemsForChildren(child, filter);
				if(Array.isArray(childItems))
				{
					items.push(...childItems);
				}
				else if(childItems)
				{
					items.push(childItems);
				}
			}

			if(!filter || parent.name.toLowerCase().includes(filter.toLowerCase()))
			{
				return <PartsCatalogItem
					// selected={highlightedPart === parent.id ? true : false} //TODO: implement selected during simulation
					key={parent.userData.guid}
					title={parent.name}
					objectGuid={parent.id}
					disableCollapse={items.length <= 0} 
					onClick={(e: MouseEvent) => {dispatch(StoreActions.partsCatalog.setSelectedPart({stepId: currentStep?.id, part: parent}))}}
					toggleHide={toggleObjectHide}
					toggleHighlight={toggleObjectHighlight}
					toggleIsolate={toggleObjectIsolate}
					hidden={simulationHiddenObjects.includes(parent.userData.guid)}
					highlighted={simulationHighlightedObjects.includes(parent.userData.guid)}
					isolated={simulationIsolatedObjects.includes(parent.userData.guid)}
					expanded={simulationExpandedObjects.includes(parent.userData.guid)}
					depth={parent.depth}
				>{items}</PartsCatalogItem>
			}
			
			return items;
		}

		const hierarchy = buildHierarchy(Engine.getInstance().getSelectionRoot(currentStep?.scene?.userData.guid));

		PartsCatalogUtils.AddObjectsToFlattenedMap(currentStep?.id, flattenHierarchy(hierarchy));

		const items : JSX.Element[] = [];

		for(const child of hierarchy)
		{
			const childItems = createItemsForChildren(child, searchTerm);
			if(Array.isArray(childItems))
			{
				items.push(...childItems);
			}
			else if(childItems)
			{
				items.push(childItems);
			}
		}

		return items;
	})();

	function isSpare(item : IPartsCatalogSceneObject) : JSX.Element
	{
		if((item.userData.partsCatalog && item.userData.partsCatalog.isSpare) || (!item.userData.partsCatalog && defaultPartsCatalogObject.isSpare))
		{
			return <span>Spare part? Yes</span>;
		}
		return <></>;
	}

	function partsKitNumber(item : IPartsCatalogSceneObject) : JSX.Element
	{
		if(item.userData.partsCatalog?.partsKitNumber && item.userData.partsCatalog.isAvailable)
		{
			return <span>Parts Kit #: {item.userData.partsCatalog.partsKitNumber}</span>;
		}
		return <></>;
	}

	function generateMetadataSpans(item : IPartsCatalogSceneObject) : JSX.Element[]
	{
		const items: JSX.Element[] = [];
		for(const metadata of partsCatalogSettings.partData.metadata)
		{
			if(metadata.id && metadata.name && metadata.data)
			{
				const data = TextVariables.textToData(metadata.data, {metaData: item.userData.metaData, partName: item.name});
				if(data.startsWith(TextVariables.variableMarker)) continue;
				items.push(<span key={metadata.id}>{metadata.name}: {data}</span>);
			}
		}

		return items;
	}

	function toggleObjectHide(guid: string)
	{
		if(simulationState !== SimulationStates.Playing) return;
		if(simulationHiddenObjects.includes(guid))
		{
			dispatch(StoreActions.partsCatalog.removeSimulationHiddenObject({stepId: currentStep.id, partId: guid}));
		}
		else
		{
			if(simulationHighlightedObjects.includes(guid))
			{
				dispatch(StoreActions.partsCatalog.removeSimulationHighlightedObject({stepId: currentStep.id, partId: guid}));
			}
			if(simulationIsolatedObjects.includes(guid))
			{
				dispatch(StoreActions.partsCatalog.removeSimulationIsolatedObject({stepId: currentStep.id, partId: guid}));
			}
			dispatch(StoreActions.partsCatalog.addSimulationHiddenObject({stepId: currentStep.id, partId: guid}));
		}
	}

	function toggleObjectHighlight(guid: string)
	{
		if(simulationState !== SimulationStates.Playing) return;
		if(simulationHighlightedObjects.includes(guid))
		{
			dispatch(StoreActions.partsCatalog.removeSimulationHighlightedObject({stepId: currentStep.id, partId: guid}));
		}
		else
		{
			if(simulationHiddenObjects.includes(guid))
			{
				dispatch(StoreActions.partsCatalog.removeSimulationHiddenObject({stepId: currentStep.id, partId: guid}));
			}
			dispatch(StoreActions.partsCatalog.addSimulationHighlightedObject({stepId: currentStep.id, partId: guid}));
		}
	}

	function toggleObjectIsolate(guid: string)
	{
		if(simulationState !== SimulationStates.Playing) return;
		if(simulationIsolatedObjects.includes(guid))
		{
			dispatch(StoreActions.partsCatalog.removeSimulationIsolatedObject({stepId: currentStep.id, partId: guid}));
		}
		else
		{
			if(simulationHiddenObjects.includes(guid))
			{
				dispatch(StoreActions.partsCatalog.removeSimulationHiddenObject({stepId: currentStep.id, partId: guid}));
			}
			dispatch(StoreActions.partsCatalog.addSimulationIsolatedObject({stepId: currentStep.id, partId: guid}));
		}
	}

	if(!partsCatalogSettings || !SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.partsCatalog)) return null;

	return (
		<Panel
			className={'uiSidebarPreviewContainer'}
		>
			<Panel>
				{selectedPart ? <>
					<div className={'partsCatalogItemTitle'}>
						<ChevronRight rotate={180} scale={1.6} onClick={() => dispatch(StoreActions.partsCatalog.setSelectedPart({stepId: currentStep?.id, part: null}))}/>
						<Spacer size={{x:'14px'}}/>
						<span>{selectedPart.name}</span>
					</div>
					<Separator orientation={LayoutOrientations.Horizontal} className={"solid"} />
					<Spacer size={{ y: "8px" }} />
					<Panel style={{ paddingLeft:'10px', lineHeight: '28px' }}>
						{isSpare(selectedPart)}
						{partsKitNumber(selectedPart)}
						{generateMetadataSpans(selectedPart)}
					</Panel>

				</> : <>
					<div className={'partsCatalogItemHolder'}>
						{showSearch ? <>
							<ChevronRight rotate={180} scale={1.6} onClick={() => {setShowSearch(false); setSearchTerm('');}}/>
							<Spacer size={{x:'8px'}}/>
							<StringProperty
								className={'partsCatalogSearchBar'}
								inputClassName={'partsCatalogSearchBarInput'}
								key={'catalogSearchBar'}
								data-name={"property-search"}
								enabledHoverBackground={false}
								enableFocusOutline={false}
								placeholder={"Search Parts"}
								value={searchTerm}
								onChangeCallback={(value) => { setSearchTerm(value) }}
								onCommitCallback={(value) => { setSearchTerm(value) }}
							/>
							{
								searchTerm !== '' ?
									<Button className={"partsCatalogClearSearch"}
										data-name={"sidebar-close-button"}
										onClick={() => {setSearchTerm('');}}
										iconRenderer={<ClearIcon scale={1.2}/>}
									/> : <></>
							}
						</> : <>
							<span className={'partsCatalogHeaderSpan'}>{partsCatalogSettings.title}</span>
							<Spacer stretchTo={{ x: "100%" }} />
							<SearchIcon className={simulationState === SimulationStates.Playing ? '' : 'unclickable'} scale={1.6} onClick={() => {setShowSearch(true)}}/>
						</>
						}
					</div>
					<Separator orientation={LayoutOrientations.Horizontal} className={"solid"} />
					<Spacer size={{ y: "8px" }} />
					<Panel //this panel contains the list items
						expand={true}
						className={simulationState === SimulationStates.Playing ? 'allowScrollY' : 'allowScrollY'}
						style={{ paddingLeft:'10px' }}>
						{simulationExpandedObjects || !simulationExpandedObjects ? renderHierarchy : renderHierarchy}
					</Panel>
				</>
				}
			</Panel>
		</Panel>
	)
}

function PartsCatalogItem(props) 
{

	const dispatch = useTypedDispatch();
	const forceUpdate = useForceUpdate();
	const currentStep = useCurrentStep();

	const simulationState = useTypedSelector(state => state.applicationModule.simulationState);
	const simulationHiddenObjects : string[] = useTypedSelector(state => 
	{
		if(currentStep?.id)
		{
			return state.partsCatalogModule.simulationHiddenObjects[currentStep.id] ?? [];
		}
		return [];
	}, idListEquals);
	const highlightedPart : string = useTypedSelector(state => state.partsCatalogModule.highlightedPartId);
	const hoveredPartId : string = useTypedSelector(state => state.partsCatalogModule.hoveredPartId);

	const itemRef = useRef<HTMLDivElement | null>(null);

	useEffect(() =>
	{
		forceUpdate();
	}, [props.expanded]);

	useEffect(() =>
	{
		if(highlightedPart === props.objectGuid)
		{
			//scroll me into view
			if(itemRef && itemRef.current && itemRef.current.scrollIntoView)
			{
				itemRef.current?.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
			}
		}
	}, [highlightedPart]);

	const 
		{ 
			onDoubleClick: originalDoubleClick, 
			onClick: originalOnClick, 
			onExpand: originalOnExpand, 
			toggleHide: originalToggleHide,
			toggleIsolate: originalToggleIsolate,
			toggleHighlight: originalToggleHighlight,
			...rest 
		} = props;

	const handleDoubleClickOverride = (e : MouseEvent) => 
	{
		e.stopPropagation();
	};

	const handleOnClickOverride = (e : MouseEvent) => 
	{
		if(originalOnClick && simulationState === SimulationStates.Playing)
		{
			dispatch(StoreActions.partsCatalog.setHoveredPartId(''));
			originalOnClick(e);
		}
		e.stopPropagation();
	};

	const handleOnExpand = (value : boolean) => 
	{
		if(!value)
		{
			dispatch(StoreActions.partsCatalog.removeSimulationExpandedObject(props.objectGuid));
		}
		else
		{
			dispatch(StoreActions.partsCatalog.addSimulationExpandedObject(props.objectGuid));
			if(props.depth > 2)
			{
				dispatch(StoreActions.partsCatalog.setShowCollapseAll(true));
			}
		}
	};

	const handleToggleHide = (e, guid: string) => 
	{
		e.stopPropagation();
		if(originalToggleHide)
		{
			originalToggleHide(guid);
		}
	};

	const handleToggleHighlight = (e, guid: string) => 
	{
		e.stopPropagation();
		if(originalToggleHighlight)
		{
			originalToggleHighlight(guid);
		}
	};

	const handleToggleIsolate = (e, guid: string) => 
	{
		e.stopPropagation();
		if(originalToggleIsolate)
		{
			originalToggleIsolate(guid);
		}
	};

	const onMouseEnter = () =>
	{
		dispatch(StoreActions.partsCatalog.setHoveredPartId(props.objectGuid));
	}

	const onMouseLeave = () =>
	{
		if(hoveredPartId === props.objectGuid)
		{
			dispatch(StoreActions.partsCatalog.setHoveredPartId(''));
		}
	}

	const checkIfParentIsHidden = () =>
	{
		if(!currentStep?.id) return false;

		function recursiveCheck(part: IPartsCatalogSceneObject) : boolean
		{
			if(part.parent)
			{
				const parent = PartsCatalogUtils.GetFlattenedPart(currentStep.id, part.parent);
				if(parent && simulationHiddenObjects.includes(parent.id))
				{
					return true;
				}
				return parent ? recursiveCheck(parent) : false;
			}
			return false;
		}

		const part = PartsCatalogUtils.GetFlattenedPart(currentStep.id, props.objectGuid);
		return part ? recursiveCheck(part) : false;
	}

	return (
		<div 
			className={'partsCatalogItem'}
			ref={itemRef}
		>
			<PropertyGroup 
				{...rest}
				className={'partsCatalogItem'+ (simulationState !== SimulationStates.Playing ? ' preview' : '')}
				iconOverride={ChevronRight}
				highlighted_th={highlightedPart === props.objectGuid ? true : false}
				opacity_th={props.hidden || checkIfParentIsHidden() ? 0.4 : 1}
				onDoubleClick={handleDoubleClickOverride}
				onClickOverride={handleOnClickOverride}
				onToggleCallback={handleOnExpand}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				expanded={props.expanded}
				decorators={[
					<div 
						key={'searchIcon'} 
						className='centerContent' 
						style={{width:'28px', height:'100%'}}
						onClick={(e) => { handleToggleIsolate(e, props.objectGuid) }}
					>
						<SearchIcon 
							fillStyle={props.isolated ? IconFillStyles.yellow : IconFillStyles.default} 
							scale={1} 
							
						/>
					</div>,
					<div 
						key={'eyeIcon'} 
						className='centerContent' 
						style={{width:'28px', height:'100%'}}
						onClick={(e) => { handleToggleHide(e, props.objectGuid) }}
					>
						{
							props.hidden ? 
								<EyeIconOff 
									fillStyle={IconFillStyles.yellow} 
									scale={1.2} 
								/> : 
								<EyeIcon 
									scale={1.2} 
								/>
						}
					</div>,
					<div 
						key={'LightbulbIcon'} 
						className='centerContent' 
						style={{width:'28px', height:'100%'}}
						onClick={(e) => { handleToggleHighlight(e, props.objectGuid) }}
					>
						<LightBulbIcon 
							fillStyle={props.highlighted ? IconFillStyles.yellow : IconFillStyles.default} 
							scale={1.75} 
						/>
					</div>,
				]}
			>
				{props.children}
			</PropertyGroup>
		</div>
	);
}